
"use client";

import { useState, useMemo, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { LogOut, LogIn } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import NavItem from "./navigation/NavItem";
import BusinessSelector from "./navigation/BusinessSelector";
import { navigationItems } from "../config/navigation";

export default function Sidebar({ open, setOpen }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [selectedBusiness, setSelectedBusiness] = useState({ id: null, name: "No Business" });

  // Default navigation items for unauthenticated or invalid states
  const defaultNavigationItems = [
    { name: "Login", href: "/login", icon: LogIn },
  ];

  // Memoize businesses to prevent re-renders
  const businesses = useMemo(() => {
    if (!user?.storeIds) return [{ id: null, name: "No Business" }];
    return user.storeIds.map((id, index) => ({
      id,
      name: `Business ${index + 1} (Store ID: ${id})`,
    }));
  }, [user?.storeIds]);

  // Generate navigation items based on user role and selected business
  const navItems = useMemo(() => {
    if (!user || !user.role || !navigationItems[user.role]) return defaultNavigationItems;
    if (user.role === "SUPER_ADMIN") {
      return navigationItems[user.role];
    }
    if (selectedBusiness && selectedBusiness.id) {
      return navigationItems[user.role].map((item) => ({
        ...item,
        href: item.href.replace(":storeId", selectedBusiness.id),
      }));
    }
    return defaultNavigationItems;
  }, [user, user?.role, selectedBusiness]);

  // Initialize selectedBusiness only on first load or business change
  useEffect(() => {
    if (!user || !businesses.length || businesses[0].id === null) return;

    const storeIdFromPath = location.pathname.match(/\/stores\/(\d+)/)?.[1];
    const matchedBusiness = businesses.find(
      (business) => business.id === parseInt(storeIdFromPath)
    );

    if (!selectedBusiness.id) {
      const newBusiness = matchedBusiness || businesses[0];
      setSelectedBusiness(newBusiness);
      // Navigate only if not on a valid store route
      if (!storeIdFromPath && user.role !== "SUPER_ADMIN") {
        navigate(`/stores/${newBusiness.id}/dashboard`, { replace: true });
      }
    } else if (storeIdFromPath && parseInt(storeIdFromPath) !== selectedBusiness.id) {
      // Sync selectedBusiness with URL if storeId changes
      const newBusiness = businesses.find(
        (business) => business.id === parseInt(storeIdFromPath)
      );
      if (newBusiness) {
        setSelectedBusiness(newBusiness);
      }
    }
  }, [user, businesses, location.pathname, selectedBusiness, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  console.log('Sidebar: location.pathname:', location.pathname);
  console.log('Sidebar: selectedBusiness:', selectedBusiness);
  console.log('Sidebar: navItems:', navItems);

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 z-20 bg-black/50 md:hidden"
          onClick={() => setOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className="fixed top-0 left-0 h-screen w-64 bg-gradient-to-b from-white to-gray-50 dark:from-gray-800 dark:to-gray-900 shadow-xl z-30">
        <div className="flex flex-col h-screen">
          {/* Logo */}
          <div className="flex-shrink-0 flex items-center justify-center h-16 border-b dark:border-gray-700 bg-white/50 dark:bg-gray-800/50 backdrop-blur">
            <h1 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent hover:scale-105 transition-transform">
              CredFlo
            </h1>
          </div>

          {/* Business Selector */}
          <div className="flex-shrink-0 p-2">
            <BusinessSelector
              businesses={businesses}
              selectedBusiness={selectedBusiness}
              onSelectBusiness={(business) => {
                if (business.id && business.id !== selectedBusiness.id) {
                  setSelectedBusiness(business);
                  if (user?.role !== "SUPER_ADMIN") {
                    navigate(`/stores/${business.id}/dashboard`, { replace: true });
                  }
                }
              }}
            />
          </div>

          {/* Navigation */}
          <div className="flex-1 overflow-y-auto px-3 py-4 space-y-2">
            <nav>
              <ul className="space-y-1">
                {navItems.map((item) => (
                  <NavItem key={item.name} {...item} />
                ))}
              </ul>
            </nav>
          </div>

          {/* Logout */}
          <div className="flex-shrink-0 p-4 border-t dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50 backdrop-blur">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 
                       rounded-lg transition-all duration-200 ease-in-out
                       hover:bg-gradient-to-r hover:from-red-500 hover:to-pink-500 hover:text-white
                       group relative overflow-hidden shadow-sm hover:shadow-md"
            >
              <LogOut className="mr-3 h-5 w-5 transition-transform group-hover:rotate-12" />
              <span className="relative z-10">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      <div className="pt-16 ml-64 flex flex-col min-h-screen">
        <main className="flex-1">
          {/* ...main content... */}
        </main>
        <footer className="...">
          {/* Footer content */}
        </footer>
      </div>
    </>
  );
}